import { Moon, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/contexts/ThemeContext';

/**
 * The theme switch on the auth screens.
 *
 * Not V11ThemeToggle: that one is drawn for the app shell — zinc track, plain
 * thumb — and the auth layout wants the accent colour on the thumb. Restyling
 * the shared control would have changed every screen behind the login.
 *
 * AL-1846: the **movement** is the shell's, though. This used to hold the thumb
 * on the right in both themes and swap the icon under it, so the control looked
 * identical whichever theme was on and only the little glyph changed. Now the
 * thumb travels — left in dark, right in light — with the icon of the other
 * theme on the side it left, which is what the rest of the platform does.
 */
export const AuthThemeToggle = () => {
  const { t } = useTranslation();
  const { resolvedTheme, setTheme } = useTheme();

  const isDark = resolvedTheme === 'dark';

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDark}
      aria-label={t('app.v11.nav.toggleTheme')}
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      className="relative inline-flex h-7 w-14 shrink-0 cursor-pointer items-center rounded-full bg-auth-heading/10 transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-auth-accent focus-visible:ring-offset-2 focus-visible:ring-offset-auth-bg"
    >
      {/* Both icons are always in the markup, each pinned to its own side, and
          only their opacity changes. Swapping which element is rendered would
          give the thumb nothing to cross and the transition nothing to animate. */}
      <span
        aria-hidden="true"
        className={[
          'absolute right-1.5 flex items-center transition-opacity duration-300',
          isDark ? 'opacity-100' : 'opacity-0',
        ].join(' ')}
      >
        <Moon className="h-3.5 w-3.5 text-auth-body" />
      </span>
      <span
        aria-hidden="true"
        className={[
          'absolute left-1.5 flex items-center transition-opacity duration-300',
          isDark ? 'opacity-0' : 'opacity-100',
        ].join(' ')}
      >
        <Sun className="h-3.5 w-3.5 text-auth-body" />
      </span>

      <span
        aria-hidden="true"
        className={[
          'pointer-events-none inline-block h-4 w-4 rounded-full bg-auth-accent transition-transform duration-300',
          isDark ? 'translate-x-1.5' : 'translate-x-[34px]',
        ].join(' ')}
      />
    </button>
  );
};
